import { Table } from "@tanstack/react-table";
import { useEffect, useRef, useState } from "react";

import { centsToEuro, euroToCents } from "#shared/helpers/prices";
import { NumberInput } from "#shared/view/designSystem/inputs/NumberInput";
import { TextInput } from "#shared/view/designSystem/inputs/TextInput";
import { Select } from "#shared/view/designSystem/Select";

export function EditableTableCell<T>(props: {
  table: Table<T>;
  rowId: string;
  columnId: string;
  initialValue: unknown;
  formatDisplayValue?: (value: unknown) => string;
  type?: "text" | "price" | "select" | "number";
  options?: { value: string; label: string }[];
  suffix?: string;
  disabled?: boolean;
  clearable?: boolean;
  min?: number;
  max?: number;
  decimalScale?: number;
}) {
  const {
    table,
    rowId,
    columnId,
    initialValue,
    formatDisplayValue,
    type = "text",
    options = [],
    suffix = "",
    disabled = false,
    clearable = false,
    min,
    max,
    decimalScale,
  } = props;
  const [value, setValue] = useState<unknown>(initialValue);
  const [isEditing, setIsEditing] = useState(false);
  const committedRef = useRef(false);

  useEffect(() => {
    setValue(initialValue);
  }, [initialValue]);

  const commit = (finalValue: unknown = value) => {
    if (committedRef.current) {
      return;
    }
    committedRef.current = true;

    setIsEditing(false);

    if (!table.options.meta?.updateData) {
      return;
    }

    requestAnimationFrame(() => table.options.meta!.updateData(rowId, columnId, finalValue));
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      commit(value);
      e.stopPropagation();
    }
  };

  if (!isEditing) {
    // oxlint-disable-next-line typescript/no-base-to-string
    let displayValue = !value ? "" : String(value);

    if (formatDisplayValue) {
      displayValue = formatDisplayValue(value);
    }

    return (
      <span
        onClick={() => {
          if (disabled) {
            return;
          }
          committedRef.current = false;
          setIsEditing(true);
        }}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            committedRef.current = false;
            setIsEditing(true);
            e.stopPropagation();
          }
        }}
        style={{
          minWidth: "50px",
          minHeight: "20px",
          cursor: "text",
          display: "block",
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
      >
        {displayValue}
      </span>
    );
  }

  if (type === "price") {
    return (
      <NumberInput
        autoFocus
        value={!value ? "" : centsToEuro(Number.parseInt(value as string))}
        onChange={(e) => setValue(euroToCents(e))}
        onBlur={() => commit(value)}
        onKeyDown={onKeyDown}
        suffix={suffix}
        min={0}
        decimalScale={2}
        decimalSeparator=","
        w="100%"
      />
    );
  }

  if (type === "select") {
    return (
      <Select
        // oxlint-disable-next-line typescript/no-base-to-string
        value={!value ? "" : String(value)}
        data={options}
        onChange={(e) => {
          setValue(e);
          commit(e);
        }}
        w="100%"
        searchable
        clearable={clearable}
      />
    );
  }

  if (type === "number") {
    return (
      <NumberInput
        autoFocus
        value={value === null || value === undefined ? "" : Number(value)}
        onChange={(e) => setValue(e)}
        onBlur={() => commit(value)}
        onKeyDown={onKeyDown}
        suffix={suffix}
        min={min}
        max={max}
        decimalScale={decimalScale}
        decimalSeparator=","
        w="100%"
      />
    );
  }

  return (
    <TextInput
      autoFocus
      // oxlint-disable-next-line typescript/no-base-to-string
      value={String(value ?? "")}
      onChange={(e) => setValue(e.target.value)}
      onBlur={() => commit(value)}
      onKeyDown={onKeyDown}
      w="100%"
    />
  );
}
